// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { partnersListSelector } from '../../feature/selectors';
import { updatePartnerDetails, getPartnerDetails } from '../../feature/actionCreators';

// Components
import { StyledPageTitle, StyledPageWrapper } from '../../components/Styled';
import { PartnerInfo, UpdatePartnerForm } from '../../components';
import {
  StyledCreateButton,
  StyledHeaderBlock,
  StyledTabsWrap,
  StyledContentBlock,
} from './PartnerDetailsContainerStyles';
import { CheckmarkIcon, EditIcon } from '@/icons';
import { PartnerDetailsDTO } from '@/modules/Partners/dtos';
import { showSuccessToast, useForm } from '@/components';
import { Skeleton } from '@/ui';

// Modules
import { usePermissions, Resource } from '@/modules/Permissions';

// Helpers
import { useBoolean } from '@/hooks';
import { IApiError } from '@/models/apiError.model';

export const UPDATE_PARTNER_FORM_NAME = 'UPDATE_PARTNER_FORM_NAME';

const PartnerDetailsContainer = () => {
  const { isLoading, partnerDetails } = useAppState(partnersListSelector);
  const { value: isEditMode, toggle: toggleEditing } = useBoolean();
  const { id } = useParams();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManagePartners = canManage(Resource.PARTNER);

  const { t } = useTranslation(['partners']);

  const handleSubmit = useCallback(async () => {
    if (!id) return false;

    try {
      const values = await form.validateFields();
      const partnerDetailsDto = new PartnerDetailsDTO(values, partnerDetails);

      await dispatch(updatePartnerDetails({ ...partnerDetailsDto, id })).unwrap();

      showSuccessToast({ message: t('partner_data_saved') });

      return true;
    } catch (error) {
      form.setFields(
        Object.entries((error as IApiError).errors ?? {}).map(([name, message]) => ({ name, errors: [message] })),
      );
      return false;
    }
  }, [id, form, dispatch, t, partnerDetails]);

  const onToggleEdit = async () => {
    if (!isEditMode) {
      form.resetFields();
      toggleEditing();
      return;
    }

    const isSaved = await handleSubmit();

    if (isSaved) {
      toggleEditing();
    }
  };

  useEffect(() => {
    dispatch(getPartnerDetails(id as string));
  }, [id]);

  if (isLoading) return <Skeleton />;

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{partnerDetails?.title}</StyledPageTitle>
        <StyledTabsWrap>
          {canManagePartners && (
            <StyledCreateButton htmlType={'submit'} onClick={onToggleEdit}>
              {isEditMode ? <CheckmarkIcon /> : <EditIcon />}
              {t(isEditMode ? 'partners_save' : 'partners_edit')}
            </StyledCreateButton>
          )}
        </StyledTabsWrap>
      </StyledHeaderBlock>
      <StyledContentBlock>
        {isEditMode && canManagePartners ? (
          <UpdatePartnerForm
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            formInstance={form}
            initialValues={partnerDetails}
          />
        ) : (
          <PartnerInfo data={partnerDetails} />
        )}
      </StyledContentBlock>
    </StyledPageWrapper>
  );
};

export default PartnerDetailsContainer;
